<script setup lang="ts">
  import Chip from '@leaflink/stash/Chip.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import Loading from '@leaflink/stash/Loading.vue';
  import { storeToRefs } from 'pinia';
  import { computed } from 'vue';

  import { LEAFLINK_ACCESS_ID, LEAFLINK_PLUS_ID, SPLIT_NAMES } from '@/constants';
  import { ProductsGetResponse } from '@/models/featureEnrollment';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import { i18n } from '@/plugins';
  import { useProductsQuery } from '@/queries/feature-enrollment/feature-enrollment.query';
  import { money } from '@/utils/i18n';

  import Container from './Container.vue';
  import StartFreeTrialButton from './StartFreeTrialButton.vue';

  const { getIsSplitEnabled } = storeToRefs(useUserStore());
  const isNueIntegrationEnabled = computed(() => {
    return getIsSplitEnabled.value(SPLIT_NAMES.ENROLLMENT_NUE_INTEGRATION);
  });

  type PerksIDs = 'marketplace' | 'directPay' | 'paymentOnSellThrough' | 'inventoryInsights' | 'accountingIntegrations';

  type Perk = {
    id: PerksIDs;
    title: string;
    description: string;
    isNew: boolean;
  };

  type ProductPerks = Record<PerksIDs, boolean>;

  type Product = Partial<ProductsGetResponse> & {
    perks: ProductPerks;
  };

  const { data: productsQuery, isFetching } = useProductsQuery({
    enabled: isNueIntegrationEnabled.value,
  });

  const isFreeTrial = computed(() => {
    const llPlusProduct = productsQuery.value?.find((product) => product.nueId === LEAFLINK_PLUS_ID);
    return llPlusProduct?.freeTrialUnit && llPlusProduct?.freeTrialUnit !== '0';
  });

  const products = computed(() => {
    /*
     * The response may return duplicate items due to how Nue system handles it.
     * Therefore, it's necessary to filter the response by valid Nue IDs.
     */
    const llAccessProduct = productsQuery.value?.find((product) => product.nueId === LEAFLINK_ACCESS_ID);
    const llPlusProduct = productsQuery.value?.find((product) => product.nueId === LEAFLINK_PLUS_ID);

    const data: Product[] = [
      // LEAFLINK ACCESS
      {
        listPrice: 0,
        name: 'LeafLink Access',
        nueId: LEAFLINK_ACCESS_ID,
        ...llAccessProduct,
        perks: {
          marketplace: true,
          directPay: true,
          paymentOnSellThrough: true,
          inventoryInsights: false,
          accountingIntegrations: false,
        },
      },

      //LEAFLINK PLUS
      {
        listPrice: 299,
        name: 'LeafLink Plus',
        nueId: LEAFLINK_PLUS_ID,
        ...llPlusProduct,
        perks: {
          marketplace: true,
          directPay: true,
          paymentOnSellThrough: true,
          inventoryInsights: true,
          accountingIntegrations: true,
        },
      },
    ];

    return data;
  });

  const PERKS: Readonly<Perk[]> = [
    {
      id: 'marketplace',
      title: i18n.t('inventoryInsights.upgradeLeafLinkPlusPage.plans.list.marketplace.title'),
      description: i18n.t('inventoryInsights.upgradeLeafLinkPlusPage.plans.list.marketplace.description'),
      isNew: false,
    },
    {
      id: 'directPay',
      title: i18n.t('inventoryInsights.upgradeLeafLinkPlusPage.plans.list.directPay.title'),
      description: i18n.t('inventoryInsights.upgradeLeafLinkPlusPage.plans.list.directPay.description'),
      isNew: false,
    },
    {
      id: 'paymentOnSellThrough',
      title: i18n.t('inventoryInsights.upgradeLeafLinkPlusPage.plans.list.paymentOnSellThrough.title'),
      description: i18n.t('inventoryInsights.upgradeLeafLinkPlusPage.plans.list.paymentOnSellThrough.description'),
      isNew: false,
    },
    {
      id: 'inventoryInsights',
      title: i18n.t('inventoryInsights.upgradeLeafLinkPlusPage.plans.list.inventoryInsights.title'),
      description: i18n.t('inventoryInsights.upgradeLeafLinkPlusPage.plans.list.inventoryInsights.description'),
      isNew: true,
    },
    {
      id: 'accountingIntegrations',
      title: i18n.t('inventoryInsights.upgradeLeafLinkPlusPage.plans.list.accountingIntegrations.title'),
      description: i18n.t('inventoryInsights.upgradeLeafLinkPlusPage.plans.list.accountingIntegrations.description'),
      isNew: false,
    },
  ];
</script>

<template>
  <section>
    <Loading v-if="isFetching" class="tw-min-h-[500px] tw-items-center" />

    <Container
      v-else
      class="lg:tw-pb-16 xl:tw-pt-36 md:max-xl:tw-px-6 md:max-xl:tw-pt-16 max-md:tw-px-4 max-md:tw-pt-6"
    >
      <header class="tw-grid md:tw-grid-cols-[7fr_2.6fr_2.4fr] max-md:tw-hidden">
        <div />

        <h3
          v-for="{ nueId, name } in products"
          :key="nueId"
          class="tw-pb-6 tw-text-center tw-text-2xl md:tw-flex md:tw-items-center md:tw-justify-center max-xl:tw-mx-3"
        >
          {{ name }}
        </h3>
      </header>

      <main
        class="tw-relative tw-z-0 before:tw-absolute before:tw-right-0 before:tw-top-0 before:tw-z-behind before:tw-hidden before:tw-h-full before:tw-w-1/5 before:tw-bg-seafoam-100 before:tw-shadow-md before:tw-content-[''] before:md:tw-block"
      >
        <div
          v-for="perk in PERKS"
          class="tw-grid tw-border-b-[1px] tw-border-b-ice-300 md:tw-grid-cols-[7fr_2.6fr_2.4fr]"
          :key="perk.title"
        >
          <div class="tw-py-6">
            <h3 class="tw-flex tw-items-center tw-gap-3 tw-text-2xl">
              <span>{{ perk.title }}</span>
              <Chip v-if="perk.isNew" color-scheme="green">
                {{ i18n.t('common.new') }}
              </Chip>
            </h3>

            <p class="tw-m-0 tw-pt-1.5 tw-text-xl">{{ perk.description }}</p>

            <div class="tw-grid tw-grid-cols-[10fr_2fr] tw-items-center tw-gap-4 tw-pt-4 md:tw-hidden">
              <template v-for="{ nueId, name, perks } in products" :key="nueId">
                <strong class="tw-text-xl tw-text-ice-900">
                  {{ name }}
                </strong>

                <div class="tw-grid tw-place-items-center">
                  <Icon v-if="perks[perk.id]" name="check" class="tw-scale-[1.5] tw-text-green-700" />
                  <Icon v-else name="close" class="tw-scale-[1.5] tw-text-ice-500" />
                </div>
              </template>
            </div>
          </div>

          <div
            v-for="{ nueId, perks } in products"
            :key="nueId"
            class="tw-hidden md:tw-flex md:tw-items-center md:tw-justify-center"
          >
            <Icon v-if="perks[perk.id]" name="check" class="tw-scale-[2] tw-text-green-700" />
            <Icon v-else name="close" class="tw-scale-[2] tw-text-ice-500" />
          </div>
        </div>
      </main>

      <footer v-if="products">
        <div class="tw-grid md:tw-grid-cols-[7fr_2.6fr_2.4fr] max-md:tw-grid-cols-2">
          <div class="max-md:tw-hidden" />
          <div
            v-for="{ nueId, name, listPrice } in products"
            :key="nueId"
            class="tw-grid tw-pb-6 tw-pt-14 tw-text-center"
          >
            <h3 class="tw-text-wrap tw-text-2xl tw-font-bold">
              <span class="tw-mb-4 tw-inline-block tw-w-28 md:tw-hidden">{{ name }}</span>
              <span class="max-md:tw-block">
                <span class="md:tw-block lg:tw-inline">{{ money(listPrice) }}/</span>
                <span class="tw-lowercase">{{ i18n.t('ageGate.month') }}</span>
              </span>
            </h3>

            <span class="tw-text-base">
              {{ i18n.t('inventoryInsights.upgradeLeafLinkPlusPage.plans.perStore') }}
            </span>
          </div>
        </div>

        <StartFreeTrialButton class="tw-ml-auto tw-block tw-w-1/5 max-lg:tw-hidden" :location="'Plans'">
          {{ isFreeTrial ? i18n.t('inventoryInsights.startFreeTrial') : i18n.t('common.activate') }}
        </StartFreeTrialButton>

        <StartFreeTrialButton
          class="tw-mx-auto tw-mb-16 tw-block lg:tw-hidden md:max-lg:tw-px-9 max-md:tw-w-full"
          :location="'Plans'"
        >
          {{
            isFreeTrial
              ? i18n.t('inventoryInsights.upgradeLeafLinkPlusPage.buttons.startFreeTrialOfLeafLinkPlus')
              : i18n.t('common.activate')
          }}
        </StartFreeTrialButton>
      </footer>
    </Container>
  </section>
</template>
